import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Toaster } from "react-hot-toast";

import AppLayout from "./layouts/appLayout/AppLayout";
import ProtectedRoute from "./layouts/adminRoutes/ProtectedRoute";
import PublicPages from "./layouts/publicPages/PublicPages";

import Home from "./pages/home/Home";
import AllCourses from "./pages/allCourses/AllCourses";
import Login from "./pages/login/Login";
import Register from "./pages/register/Register";
import PageNotFound from "./pages/pageNotFound/PageNotFound";
import CourseWatching from "./pages/courseWatching/CourseWatching";
import Settings from "./pages/settings/Settings";
import GoogleLoginReturn from "./pages/googleLoginReturn/GoogleLoginReturn";
import { ThankYou } from "./features/payments/pages/ThankYou";
import { Course } from "./pages/course/Course";
import PaymentPlans from "./pages/paymentPlans/PaymentPlans";
import ForgotPassword from "./pages/forgotPassword/ForgotPassword";
import Help from "./pages/help/Help";
import PrivacyPolicy from "./pages/privacyPolicy/PrivacyPolicy";
import TermsAndConditions from "./pages/termsAndConditions/TermsAndConditions";
import Impressum from "./pages/impressum/Impressum";

import UserProfile from "./pages/userProfile/UserProfile";
import ProfileSavedCourses from "./features/auth/components/profileSavedCourses/ProfileSavedCourses";
import ProfileLikedVideos from "./features/auth/components/profileLikedVideos/ProfileLikedVideos";

import Plans from "./pages/plans/Plans";

import FullPageLoader from "./ui/fullPageLoader/FullPageLoader";
import UmamiWrapper from "./utils/UmamiWrapper";
import { useMediaQuery } from "@/hooks/useMediaQuery";

const AdminRouter = React.lazy(() => import("./features/admin/AdminRouter"));

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: 0,
			gcTime: 0,
		},
	},
});

function App() {
	const isSmallScreen = useMediaQuery("(max-width: 1100px)");

	return (
		<>
			<QueryClientProvider client={queryClient}>
				<BrowserRouter>
					<Routes>
						<Route element={<UmamiWrapper />}>
							<Route element={<PublicPages />}>
								<Route element={<AppLayout />}>
									<Route path="/" element={<Home />} />
									<Route
										path="thank-you"
										element={<ThankYou />}
									/>
									<Route path="courses">
										<Route index element={<AllCourses />} />
									</Route>
									<Route path="settings">
										<Route
											index
											element={
												<Settings currentTab="settings" />
											}
										/>
										<Route
											path="change-password"
											element={
												<Settings currentTab="change-password" />
											}
										/>
										<Route
											path="billing"
											element={
												<Settings currentTab="billing" />
											}
										/>
										<Route
											index
											element={
												<Settings currentTab="settings" />
											}
										/>
									</Route>
									<Route
										path="u/:username"
										element={<UserProfile />}
									>
										<Route
											index
											element={<ProfileSavedCourses />}
										/>
										<Route
											path="liked-videos"
											element={<ProfileLikedVideos />}
										/>
									</Route>
									<Route path="plans" element={<Plans />} />
								</Route>
								<Route
									path="courses/:courseSlug"
									element={<Course />}
								/>
								<Route
									path="courses/:courseSlug/:moduleSlug/:unitSlug"
									element={<CourseWatching />}
								/>
							</Route>
							<Route path="login">
								<Route index element={<Login />} />
								<Route
									path="google"
									element={<GoogleLoginReturn />}
								/>
							</Route>

							<Route path="register" element={<Register />}>
								<Route
									path="plans"
									element={<PaymentPlans />}
								/>
							</Route>

							<Route
								path="forgot-password"
								element={<ForgotPassword />}
							/>
							<Route path="support" element={<Help />} />
							<Route
								path="admin/*"
								element={
									<ProtectedRoute>
										<React.Suspense
											fallback={<FullPageLoader />}
										>
											<AdminRouter />
										</React.Suspense>
									</ProtectedRoute>
								}
							/>
							<Route
								path="privacy-policy"
								element={<PrivacyPolicy />}
							/>
							<Route
								path="terms-and-conditions"
								element={<TermsAndConditions />}
							/>
							<Route path="impressum" element={<Impressum />} />
							<Route path="*" element={<PageNotFound />} />
						</Route>
					</Routes>
				</BrowserRouter>
				<Toaster
					position={isSmallScreen ? "top-center" : "bottom-center"}
					reverseOrder={true}
				/>

				<ReactQueryDevtools />
			</QueryClientProvider>
		</>
	);
}

export default App;
